import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, CircularProgress } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { clearCurrentDirectoryHierarchy, selectDirectoryHierarchy, selectFetchDirectoryHierarchyStatus } from '../../features/FileManager/FileManagerSlice';
import { fetchFileDirectories } from '../../features/FileManager/FileManagerActions';
import DirectoryTree from '../fileManagement/DirectoryTree';
import { DataLakeDirectoryModel } from '../../gql-types.generated';
import ErrorMessage from '../ErrorMessage';

interface SelectDirectoryDialogProps {
    isOpen: boolean;
    rootParentPath?: string; // Optional root parent path to limit directory selection
    onClose: () => void;
    onMove: (destinationPath: string) => void;
    disabledPaths: string[];
    error?: Error;
}

const SelectDirectoryDialog: React.FC<SelectDirectoryDialogProps> = (props) => {
    const { isOpen, rootParentPath, onClose, onMove, disabledPaths, error } = props;
    const dispatch = useAppDispatch();
    const directoryHierarchy = useAppSelector(selectDirectoryHierarchy);
    const fetchStatus = useAppSelector(selectFetchDirectoryHierarchyStatus);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedPath, setSelectedPath] = useState<string>();
    const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
    
    useEffect(() => {
        if (isOpen) {
            // Only fetch if we don't already have the data
            if (!directoryHierarchy) {
                setIsLoading(true);
                dispatch(fetchFileDirectories(rootParentPath));
            }
        } else {
            // Reset state when dialog is closed
            setIsLoading(false);
            dispatch(clearCurrentDirectoryHierarchy());
            setSelectedPath(undefined);
            setExpandedPaths(new Set());
        }
    }, [isOpen, directoryHierarchy]);

    useEffect(() => {
        if (fetchStatus?.result) {
            setIsLoading(false);
        }
    }, [fetchStatus]);

    useEffect(() => {
        if (error) {
            setIsLoading(false);
        }
    }, [error]);

    const handleDirectoryClick = useCallback((path: string) => {
        // Don't allow selecting disabled paths
        if (!disabledPaths.includes(path)) {
            setSelectedPath(path);
        }
    }, [disabledPaths]);

    const handleToggleExpand = useCallback((path: string) => {
        setExpandedPaths(prev => {
            const newSet = new Set(prev);
            if (newSet.has(path)) {
                newSet.delete(path);
            } else {
                newSet.add(path);
            }
            return newSet;
        });
    }, []);

    const handleMove = useCallback(() => {
        if (selectedPath) {
            onMove(selectedPath);
        }
    }, [selectedPath, onMove]);

    const getExpandedPaths = (): Set<string> => {
        // Always ensure the root parent path is expanded
        const newSet = new Set(expandedPaths);
        if (rootParentPath) {
            newSet.add(rootParentPath);
        }
        return newSet;
    };

    const getStartingPathDisplay = (): string => {
        if (rootParentPath && rootParentPath.lastIndexOf('/') > -1){
            return `${rootParentPath.substring(0, rootParentPath.lastIndexOf('/'))}/`;
        } 
        return '';  
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Select Destination Directory</DialogTitle>
            <DialogContent>
                {error && (
                    <Box mb={2}>
                        <ErrorMessage error={error} />
                    </Box>
                )}
                <Box sx={{ minHeight: '400px', maxHeight: '400px', overflow: 'auto', width: '100%' }}>
                    {isLoading && (
                        <Box display="flex" justifyContent="center" my={2}>
                            <CircularProgress />
                        </Box>
                    )}
                    {!isLoading && directoryHierarchy && (
                        <>
                        { rootParentPath &&  (
                        <Box>
                            <Typography>
                                {getStartingPathDisplay()}
                            </Typography>
                        </Box>
                        )}
                        <DirectoryTree
                            directoryHierarchy={directoryHierarchy}
                            expandedPaths={getExpandedPaths()}
                            activeDirectory={
                                selectedPath 
                                    ? { path: selectedPath, name: '' } as DataLakeDirectoryModel 
                                    : undefined
                            }
                            onDirectoryClick={handleDirectoryClick}
                            onToggleExpand={handleToggleExpand}
                            disabledPaths={disabledPaths}
                        />
                        </>
                    )}
                </Box>
                <Box mt={1}>
                    <Typography visibility={selectedPath ? 'visible' : 'hidden'}>
                        The selected file(s) will be moved to directory: {selectedPath}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button 
                    onClick={handleMove}
                    disabled={!selectedPath}
                    variant="contained"
                    color="primary"
                >
                    Move
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SelectDirectoryDialog;
